import React, { useCallback, useState } from 'react';
import cx from 'classnames';
import { removeStatusCodeMetaTag, setStatusCodeMetaTag } from 'helpers/metaTags';
import { eventClickHandler } from 'helpers/prematch';

import {
  ActiveSportServiceEventView,
  ArchivedSportServiceEventView,
  btag,
  constants,
  get,
  getConfigProp,
  isEmptyCollection,
  isEventOffline,
  isHidden,
  isInactive,
  isSuspended,
  PrematchSportServiceEventView,
  pushToDataLayer,
  SiteConfigManager,
  StorageManager,
  UnspecifiedSportServiceEventView,
  useArchivedEventState,
  useBetslipState,
  useEventState,
  usePreferences,
  useSettings,
  useUpdateEffect,
} from '@atlas/core';
import { EventDetailsPrematchBanner, useEventViewHandlers } from '@atlas/ui';

import { FAVORITE_MARKET_GROUPS, MATCH_WINNER_SPORT_IDS } from 'common/constants';
import { CDN_TYPE_CMS, get1X2Outcomes, getCDNUrl } from 'common/utils/utils';
import EventNotFound from 'components/EventNotFound/EventNotFound';
import FreeBetNotification from 'components/FreeBetNotification/FreeBetNotification';
import useEventMarketGroups from 'components/Pages/Details/useEventMarketGroups';
import SeoPageText from 'components/SeoPage/SeoPageText';

import GameStats from './GameStats/GameStats';
import DetailsEventHeader from './DetailsEventHeader';
import DetailsEventRedirectPage from './DetailsEventRedirectPage';
import DetailsMarketTypesList from './DetailsMarketTypesList';
import DetailsOutcomeStatusesTranscription from './DetailsOutcomeStatusesTranscription';
import EventDetailsControls from './EventDetailsControls';
import LiveCenter from './LiveCenter';

const {
  STATUS: { ACTIVE, HIDDEN },
} = constants;

const getMatchWinnerOutcomes = marketTypes => {
  const matchWinnerId = Object.keys(marketTypes).find(id =>
    MATCH_WINNER_SPORT_IDS.includes(Number(id)),
  );
  const markets = marketTypes[matchWinnerId]?.markets;
  const matchWinnerOutcomes =
    markets && Object.values(markets).find(market => market.status === ACTIVE)?.outcomes;

  return get1X2Outcomes(matchWinnerOutcomes ? Object.values(matchWinnerOutcomes) : []);
};

const DetailsEvent = ({ isIframe, isResults = false }) => {
  const { lang, quickBet: isQuickBet } = useSettings();
  const eventState = isResults ? useArchivedEventState : useEventState;
  const event = eventState();
  const showTopNavigation = SiteConfigManager.getConfig(
    'config.config.sportsbook.showTopNavigationMobile',
  );
  const showBottomNavigation = SiteConfigManager.getConfig(
    'config.config.sportsbook.showBottomNavigationMobile',
  );

  const disclaimerConfig = SiteConfigManager.getConfig(
    'config.config.sportsbook.scoreboardWarning',
  );

  const isSportRadarProject = getConfigProp('isSportRadarProject');

  const { marketTypes, outright, source, sportId, sportService } = event;

  const onEventClick = useEventViewHandlers(event, eventClickHandler);

  const { activeGroup, marketGroups, setActiveGroup } = useEventMarketGroups(event);

  const isLiveEvent = sportService === constants.SPORT_SERVICE.LIVE;
  // save market show status
  const localStorageData = React.useMemo(() => {
    try {
      const marketTypesState = JSON.parse(StorageManager.getItem('marketTypesState') || []);
      const marketTypesStateBySportId = get(marketTypesState, sportId);

      const currentExpandedMarketTypes = Object.values(marketTypes).reduce(
        (result, { id }) => ({ ...result, [id]: !!marketTypesStateBySportId[id] }),
        {},
      );

      return currentExpandedMarketTypes;
    } catch (e) {
      return {};
    }
  }, [marketTypes, sportId]);

  React.useEffect(() => {
    if (event.id) {
      const outcomes = getMatchWinnerOutcomes(event.marketTypes);

      pushToDataLayer(
        'open_detail_page',
        {
          id: event.id,
          language: lang,
          outcomes,
          participants: event.participants,
          sportId: event.sportId,
          sportName: event.sportName,
          startTime: new Date(event.startTime).toISOString(),
          status: isLiveEvent ? 'live' : 'not_started',
          tournamentId: event.tournamentId,
          tournamentName: event.tournamentName,
        },
        btag,
      );
    }
  }, [event.id]);

  React.useEffect(() => {
    if (event.error || event.sportService === constants.SPORT_SERVICE.ARCHIVED) {
      setStatusCodeMetaTag();
    }
    return () => removeStatusCodeMetaTag();
  }, [event.error, event.sportService]);

  const [expandedMarketTypes, setExpandedMarketTypes] = useState(localStorageData);

  const handleSetShrinkMarketTypes = useCallback(
    marketTypeIdsToSet => {
      const newMarketTypesShowStateString = {
        ...expandedMarketTypes,
        [sportId]: marketTypeIdsToSet,
      };

      setExpandedMarketTypes(marketTypeIdsToSet);
      StorageManager.setItem('marketTypesState', JSON.stringify(newMarketTypesShowStateString));
    },
    [sportId],
  );

  const {
    settings: { feeds },
  } = useBetslipState();
  const preferences = usePreferences();

  const isEventDisabled = isInactive(event);

  const suspended =
    isEventOffline({ feeds, source, sportId, sportService }) ||
    (isSuspended(event) && sportService !== constants.SPORT_SERVICE.ARCHIVED);

  /*
    Special archived warning flag for event details
  */
  const [isArchivedWarningDisplayed, setIsArchivedWarningDisplayed] = useState(false);
  /*
    Sport service change handler
    If the client is on the detailed page of the event and it has ended
    we showing this view
    If the client receives a link to an event that has ended, then
    immediately showing detailed results page without warning
  */
  useUpdateEffect(() => {
    if (event.sportService === constants.SPORT_SERVICE.ARCHIVED && !isResults) {
      setIsArchivedWarningDisplayed(true);
    }
  }, [event.sportService, isResults]);

  const { favoriteMarketTypes, selectFavoriteMarketType } = preferences;

  const nonEmptyMarketTypes = React.useMemo(() => {
    if (!marketTypes) {
      return [];
    }

    return Object.values(marketTypes)
      .map(marketType =>
        Object.values(marketType.markets).reduce(
          (acc, market, marketId) => {
            const oddsData = Object.values(market.outcomes).reduce(
              (outcomesAcc, outcomeData) => {
                const state = {
                  hasOutcomeLessOne: outcomesAcc.hasOutcomeLessOne,
                };

                state.length = outcomesAcc.length + 1;
                if (!outcomesAcc.hasOutcomeLessOne && outcomeData.odds < 1) {
                  state.hasOutcomeLessOne = true;
                }

                return state;
              },
              { hasOutcomeLessOne: false, length: 0 },
            );
            const marketData = {
              data: {
                ...acc.data,
                markets: {
                  ...acc.data.markets,
                  [marketId]: market,
                },
              },
              isEmpty: false,
            };
            if (!isHidden(market) && (!oddsData.hasOutcomeLessOne || oddsData.length > 3)) {
              acc = marketData;
            } else if (
              isHidden(market) &&
              event.sportService === constants.SPORT_SERVICE.ARCHIVED
            ) {
              acc = marketData;
            }

            return acc;
          },
          { data: { ...marketType, markets: {} }, isEmpty: true },
        ),
      )
      .filter(({ isEmpty }) => !isEmpty);
  }, [event.sportService, marketTypes]);

  const marketTypesWithFavorites = React.useMemo(
    () =>
      nonEmptyMarketTypes?.map(marketType => ({
        ...marketType,
        isFavorite: favoriteMarketTypes.includes(marketType.data.id),
      })),
    [nonEmptyMarketTypes, favoriteMarketTypes],
  );

  const filteredMarketTypes = React.useMemo(() => {
    if (!activeGroup || !activeGroup.group) {
      return marketTypesWithFavorites;
    }

    if (activeGroup.group === FAVORITE_MARKET_GROUPS) {
      return marketTypesWithFavorites.filter(({ isFavorite }) => isFavorite);
    }

    return marketTypesWithFavorites.filter(({ data }) => data.groups.includes(activeGroup.group));
  }, [marketTypesWithFavorites, activeGroup]);

  const sortedMarketTypes = React.useMemo(
    () =>
      filteredMarketTypes.sort((a, b) => {
        if (a.isFavorite === b.isFavorite) {
          return a.data.orderPosition - b.data.orderPosition;
        }

        return b.isFavorite - a.isFavorite;
      }),
    [filteredMarketTypes],
  );

  const eventDetailsControlRender = React.useMemo(() => {
    if (isEmptyCollection(sortedMarketTypes)) {
      return null;
    }

    if (outright || event.sportService === constants.SPORT_SERVICE.ARCHIVED) return null;

    return (
      <EventDetailsControls
        activeGroup={activeGroup}
        event={event}
        expandedMarketTypes={expandedMarketTypes}
        handleSetShrinkMarketTypes={handleSetShrinkMarketTypes}
        marketGroups={marketGroups}
        marketTypes={marketTypes}
        setActiveGroup={setActiveGroup}
      />
    );
  }, [
    sortedMarketTypes,
    marketGroups,
    activeGroup,
    event,
    setActiveGroup,
    handleSetShrinkMarketTypes,
    expandedMarketTypes,
    marketTypes,
  ]);

  const isSimplifiedView = React.useMemo(
    () => SiteConfigManager.getConfig('config.config.sportsbook.mobileGridViewType'),
    [],
  );

  const detailsEventClass = cx(
    'details-event-container',
    { quickbet: isQuickBet },
    { 'no-navbar': !showBottomNavigation },
    { 'details-in-iframe': isIframe },
    { 'no-top-nav': showTopNavigation === false },
    { 'is-live-page': isLiveEvent },
    { 'simplified-view': isSimplifiedView === 'simplified' },
    {
      'empty-market-types': isEmptyCollection(sortedMarketTypes) || event.status === HIDDEN,
    },
  );

  if (event.error) {
    return <EventNotFound />;
  }

  if (isArchivedWarningDisplayed) {
    return (
      <DetailsEventRedirectPage
        detailsEventClass={detailsEventClass}
        isIframe={isIframe}
        onHidePopup={() => setIsArchivedWarningDisplayed(false)}
        suspended={suspended}
      />
    );
  }

  return (
    <div className="detail-page">
      <div className={detailsEventClass} onClick={onEventClick}>
        <ActiveSportServiceEventView>
          {!isSportRadarProject && <FreeBetNotification />}
        </ActiveSportServiceEventView>

        <DetailsEventHeader
          disclaimer={isLiveEvent && disclaimerConfig}
          event={event}
          isResults={isResults}
          suspended={suspended}
        />

        {event.info && <LiveCenter event={event} />}

        <UnspecifiedSportServiceEventView>
          <GameStats event={event} />
        </UnspecifiedSportServiceEventView>

        <ArchivedSportServiceEventView>
          {!isEmptyCollection(event.marketTypes) && <GameStats event={event} />}
        </ArchivedSportServiceEventView>

        {isResults && !isEmptyCollection(event.marketTypes) && <GameStats event={event} />}

        <PrematchSportServiceEventView>
          {!outright && (
            <EventDetailsPrematchBanner
              cdnHostname={getCDNUrl(CDN_TYPE_CMS)}
              event={event}
              isMobile
            />
          )}
        </PrematchSportServiceEventView>

        {eventDetailsControlRender}

        <ArchivedSportServiceEventView>
          {!isEmptyCollection(event.marketTypes) && <DetailsOutcomeStatusesTranscription />}
        </ArchivedSportServiceEventView>

        {isResults && !isEmptyCollection(event.marketTypes) && (
          <DetailsOutcomeStatusesTranscription />
        )}

        <DetailsMarketTypesList
          event={event}
          expandedMarketTypes={expandedMarketTypes}
          handleSetShrinkMarketTypes={handleSetShrinkMarketTypes}
          isEventDisabled={isEventDisabled}
          processedMarketTypes={sortedMarketTypes}
          selectFavoriteMarketType={selectFavoriteMarketType}
          suspended={suspended}
        />
        <SeoPageText />
      </div>
    </div>
  );
};

export default DetailsEvent;
